import React from 'react';

/**
 * Intent Impact sheet — shown when an intent *starts a journey* rather than
 * being a one-off action. It previews the downstream path ("this begins
 * onboarding, here's what's involved") so the operator understands the
 * consequence before committing, and can either start the guided flow or just
 * do the single action.
 *
 * Deterministic: the steps are passed in (computed/declared by the caller),
 * not generated. This is the "impact analysis" layer of the intent flow.
 */

export interface ImpactStep {
  label: string;
  hint?: string;
}

export function IntentImpactSheet({
  open,
  title,
  intro,
  steps,
  primaryLabel,
  secondaryLabel,
  accent = '#6366f1',
  onPrimary,
  onSecondary,
  onClose,
}: {
  open: boolean;
  title: string;
  intro: string;
  steps: ImpactStep[];
  primaryLabel: string;
  secondaryLabel: string;
  accent?: string;
  onPrimary: () => void;
  onSecondary: () => void;
  onClose: () => void;
}) {
  if (!open) return null;
  return (
    <div style={styles.scrim} onMouseDown={onClose}>
      <div style={styles.sheet} onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={title}>
        <div style={{ ...styles.head, background: `linear-gradient(135deg, ${accent}, ${accent}cc)` }}>
          <span style={styles.headIcon}>🧭</span>
          <div>
            <div style={styles.headTitle}>{title}</div>
            <div style={styles.headIntro}>{intro}</div>
          </div>
          <button style={styles.close} onClick={onClose} aria-label="Close">×</button>
        </div>

        <div style={styles.body}>
          <div style={styles.pathLabel}>The path ahead</div>
          <ol style={styles.steps}>
            {steps.map((s, i) => (
              <li key={i} style={styles.step}>
                <span style={{ ...styles.stepNum, borderColor: accent, color: accent }}>{i + 1}</span>
                <span style={styles.stepBody}>
                  <span style={styles.stepLabel}>{s.label}</span>
                  {s.hint && <span style={styles.stepHint}>{s.hint}</span>}
                </span>
                {i < steps.length - 1 && <span style={styles.connector} aria-hidden />}
              </li>
            ))}
          </ol>
        </div>

        <div style={styles.actions}>
          <button style={styles.secondary} onClick={onSecondary}>{secondaryLabel}</button>
          <button style={{ ...styles.primary, background: accent }} onClick={onPrimary}>{primaryLabel}</button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  scrim: { position: 'fixed', inset: 0, background: 'rgba(8,11,22,0.5)', backdropFilter: 'blur(3px)', zIndex: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' },
  sheet: { width: 'min(520px, 96vw)', background: '#fff', borderRadius: 18, overflow: 'hidden', boxShadow: '0 30px 80px -20px rgba(8,11,22,0.6)' },
  head: { display: 'flex', alignItems: 'flex-start', gap: 14, padding: '20px 22px', color: '#fff', position: 'relative' },
  headIcon: { fontSize: '1.6rem', lineHeight: 1 },
  headTitle: { fontSize: '1.15rem', fontWeight: 800 },
  headIntro: { fontSize: '0.85rem', opacity: 0.9, marginTop: 2, lineHeight: 1.4 },
  close: { position: 'absolute', top: 14, right: 16, background: 'transparent', border: 'none', color: '#fff', fontSize: '1.5rem', cursor: 'pointer', lineHeight: 1, opacity: 0.8 },
  body: { padding: '18px 22px 4px' },
  pathLabel: { fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#8a93a6', marginBottom: 12 },
  steps: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 2 },
  step: { position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 12, padding: '8px 0' },
  stepNum: { flexShrink: 0, width: 26, height: 26, borderRadius: '50%', border: '2px solid', display: 'grid', placeItems: 'center', fontSize: '0.8rem', fontWeight: 800, background: '#fff', zIndex: 1 },
  stepBody: { display: 'flex', flexDirection: 'column' },
  stepLabel: { fontSize: '0.9rem', fontWeight: 700, color: '#0b1120' },
  stepHint: { fontSize: '0.76rem', color: '#6b7280', marginTop: 1 },
  connector: { position: 'absolute', left: 12, top: 34, bottom: -2, width: 2, background: '#e8eaf0' },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 22px 20px' },
  secondary: { background: '#eef0f4', color: '#334155', border: 'none', borderRadius: 10, padding: '10px 16px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' },
  primary: { color: '#fff', border: 'none', borderRadius: 10, padding: '10px 18px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' },
};
